/* ──────────────────────────────────────────────────────────────
   src/hooks/usePivot.ts
   Maneja relaciones N:M profesor → (asignaturas | formatos | …)
   ────────────────────────────────────────────────────────────── */
import { useState, useCallback, useEffect } from "react";
import api from "../api/axios";

/* recursos admitidos  (plural, tal y como aparecen en la URL) */
export type ProfesorPivotResource = "asignaturas" | "formatos" | "centros" | "idiomas" | "tipos";

/**
 *  Genera helpers (rows, add, remove) para un endpoint con patrón:
 *
 *    GET    /base/:idProfesor/:resource
 *    POST   /base/:idProfesor/:resource           { idDestino }
 *    DELETE /base/:idProfesor/:resource/:idDestino
 */
export function usePivot(baseUrl: string, profesorId: number, resource: ProfesorPivotResource) {
  const [rows, setRows] = useState<any[]>([]);

  /* --- leer listado --- */
  const refresh = useCallback(
    () => api.get(`${baseUrl}/${profesorId}/${resource}`).then((r) => setRows(r.data)),
    [baseUrl, profesorId, resource]
  );

  /* --- añadir vínculo --- */
  const add = (idDestino: number) =>
    api
      .post(`${baseUrl}/${profesorId}/${resource}`, {
        [`id${ucFirst(resource.slice(0, -1))}`]: idDestino, // idAsignatura / idCentro …
      })
      .then(refresh);

  /* --- eliminar vínculo --- */
  const remove = (idDestino: number) =>
    api.delete(`${baseUrl}/${profesorId}/${resource}/${idDestino}`).then(refresh);

  /* auto-refresh al montar / cambiar profesor */
  useEffect(() => {
    if (profesorId) refresh();
    else setRows([]);
  }, [refresh, profesorId]);

  return { rows, add, remove, refresh };
}

/* helper → “centros” → “Centro”, “idiomas” → “Idioma” … */
function ucFirst(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
